import { api } from './client';
import type { VisitStatus, VisitPriority, LegalEntity } from '@shared/types';

// ==================== Types ====================

export type OwnershipType = 'own' | 'delegated' | 'subordinate' | 'additionalOffice';

export interface ClientInfo {
  id: number;
  name: string;
  legalName: string | null;
  address: string | null;
  city: string | null;
  phone: string | null;
  legalEntity: string;
  inn: string | null;
  latitude: number | null;
  longitude: number | null;
  // Сетевые данные (для /etc/network/vars)
  login: string | null;
  password: string | null;
}

export interface CurrentVisitInfo {
  id: string;
  status: VisitStatus;
  priority: VisitPriority;
  plannedDate: string | null;
  plannedTime: string | null;
  note: string | null;
  startedAt: string | null;
}

export interface ClientPreferenceInfo {
  preferredDayOfWeek: number | null;
  preferredTime: string | null;
  note: string | null;
}

export interface FinanceSummary {
  // Юрлицо
  legalEntity: LegalEntity;
  // Баланс: положительный = переплата клиента, отрицательный = долг клиента
  balance: number;
  // Статус: 'ok' | 'debt' | 'overpayment'
  status: 'ok' | 'debt' | 'overpayment';
  // Сумма актов (услуги оказаны)
  actsTotal: number;
  // Сумма платежей
  paymentsTotal: number;
  // Дата последнего платежа
  lastPaymentDate: string | null;
}

export interface DocumentsSummary {
  total: number;     // Всего несданных документов
  danger: number;    // 21-40 дней
  critical: number;  // 41+ дней
}

export interface MobileDetailResponse {
  client: ClientInfo;
  currentVisit: CurrentVisitInfo | null;
  preference: ClientPreferenceInfo | null;
  ownershipTypes: OwnershipType[];
  financeSummaries: FinanceSummary[];
  documentsSummary: DocumentsSummary;
  canDelegate: boolean;
}

// ==================== Invoice Summary ====================

export interface InvoiceSummaryItem {
  id: string;
  number: string;
  date: string;
  amount: number;
  pcCount: number;
  pricePerPc: number;
  serverCount: number;
  pricePerServer: number;
  paymentState: 'PAID' | 'UNPAID';
  overdueDays: number | null;
  hasServices: boolean;
  servicesTotal: number;
  employee: { firstName: string; lastName: string } | null;
  offices: Array<{
    name: string;
    employeeLastName: string | null;
    accountedPCs: number;
  }>;
}

// ==================== API ====================

export interface ResolveOfficeResponse {
  clientId: number;
  officeId: string;
  officeName: string;
}

export const clientDetailApi = {
  getMobileDetail: async (clientId: number, officeId?: string): Promise<MobileDetailResponse> => {
    const params: Record<string, string> = {};
    if (officeId) params.officeId = officeId;
    const { data } = await api.get(`/clients/${clientId}/mobile-detail`, { params });
    return data;
  },

  resolveOffice: async (officeCode: number): Promise<ResolveOfficeResponse> => {
    const { data } = await api.get(`/clients/resolve-office/${officeCode}`);
    return data;
  },

  getInvoiceSummary: async (clientId: number): Promise<InvoiceSummaryItem[]> => {
    const { data } = await api.get(`/clients/${clientId}/invoice-summary`);
    return data;
  },
};
